/* eslint-disable no-fallthrough */
import { fetchSessionObject, isEmptyString, showConcessionCardSection } from '@/utils';
import { CoverComposition, NeedsAnalysisEligibility, PaymentMethods } from '@general/js/enums';
import {
  STORAGE_KEYS,
  removeWhitespaceFromString,
  formatFormDate,
  isYearLeapYear,
  getDateWithLastDay,
  removeBSBMask,
  calculateAge,
  reverseFormatFormDate,
  removeCaeForCertainLhcReasons,
  mapIpsiInstrumentTypeArray,
} from '@general/js/utils';

// Since there are 5 possible fields, any more than that would be considered an empty address.
const MAX_EMPTY_ADDRESS_FIELDS = 4;
let ELIGIBILITY_KEY = 'eligibility';

const getStorageKeys = (composition, eligibility) => {
  const storageKeys = [];
  switch (composition) {
    case CoverComposition.FAMILY:
    case CoverComposition.FAMILY_PLUS:
      storageKeys.push(STORAGE_KEYS.JOIN_NOW.CHILDREN_FEATURE);
      storageKeys.push(STORAGE_KEYS.JOIN_NOW.PARTNER_FEATURE);
    case CoverComposition.COUPLE:
    case CoverComposition.SINGLE_PARENT:
    case CoverComposition.SINGLE_PARENT_PLUS:
    case CoverComposition.CHILDREN_ONLY:
      if (!storageKeys.includes(STORAGE_KEYS.JOIN_NOW.CHILDREN_FEATURE) && composition !== CoverComposition.COUPLE) {
        storageKeys.push(STORAGE_KEYS.JOIN_NOW.CHILDREN_FEATURE);
      } else if (composition === CoverComposition.COUPLE) {
        storageKeys.push(STORAGE_KEYS.JOIN_NOW.PARTNER_FEATURE);
      }
    case CoverComposition.SINGLE:
      storageKeys.push(STORAGE_KEYS.JOIN_NOW.POLICY_HOLDER_FEATURE);
    default:
      switch (eligibility) {
        case NeedsAnalysisEligibility.SERVING:
          storageKeys.push(STORAGE_KEYS.JOIN_NOW.ELIGIBILITY.CURRENTLY_SERVING);
          break;
        case NeedsAnalysisEligibility.EX_SERVING:
          storageKeys.push(STORAGE_KEYS.JOIN_NOW.ELIGIBILITY.EX_SERVING);
          break;
        case NeedsAnalysisEligibility.WORK_CONNECTION:
          storageKeys.push(STORAGE_KEYS.JOIN_NOW.ELIGIBILITY.WORK_CONNECTION);
          break;
        case NeedsAnalysisEligibility.RELATED:
          storageKeys.push(STORAGE_KEYS.JOIN_NOW.ELIGIBILITY.SUPPORTING);
          break;
        default:
          logger.error('CreateMembershipUtils - getStorageKeys() is missing eligibility');
          break;
      }
      storageKeys.push(
        STORAGE_KEYS.JOIN_NOW.REBATE_FEATURE,
        STORAGE_KEYS.JOIN_NOW.FUTURE_PAYMENT.FEATURE,
        STORAGE_KEYS.JOIN_NOW.FUTURE_PAYMENT.CREDIT_CARD,
        STORAGE_KEYS.JOIN_NOW.CONFIRMATION_FEATURE,
      );
      break;
  }

  return storageKeys;
};

const isNotBlankString = (str) => {
  return !isEmptyString(str);
};

export const mapAddress = (address, isPostal, isPOBox) => {
  if (address && isPOBox === true && isPostal === true) {
    // Postal address and it is used a PO Box, it will need to convert to the EAI expected information
    const addressObject = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      addressLine3: address.addressLine3,
      suburb: address.suburb,
      postcode: address.postcode,
      state: address.state,
      dpId: address.dpId,
    };
    return addressObject;
  }

  if (address && isPOBox !== true) {
    // Check through all the values in the address and if only the state is not blank, then the address is not valid
    const addressKeys = Object.keys(address);
    let emptyCount = 0;
    addressKeys.forEach((addressKey) => {
      if (addressKey !== 'state') {
        if (address[addressKey] === '') {
          emptyCount += 1;
        }
      }
    });

    // if address object is empty or number of keys are morethan max number it allows, return null
    if (emptyCount > MAX_EMPTY_ADDRESS_FIELDS) {
      return null;
    }

    const addressObject = { ...address };
    addressObject.dpId = address.dpId;

    return addressObject;
  }

  return null;
};

const mapNamesGenderAndDOB = (personData, isChangeCover = false) => {
  const personObject = {
    dateOfBirth: formatFormDate(personData.DOB, true),
    firstName: personData.firstName,
    lastName: personData.lastName,
    gender: personData.sex,
  };

  if (!isChangeCover) {
    personObject.gender = personData.sex;
  }

  if (isNotBlankString(personData.middleName)) {
    personObject.middleName = personData.middleName;
  }

  return personObject;
};

const mapPreviousFunds = (personData) => {
  const transferFunds = [];
  personData.selectedInsuranceFund.forEach((selectedFund) => {
    transferFunds.push({ fundCode: selectedFund.name, memberNumber: selectedFund.number });
  });
  return transferFunds;
};

export const mapPerson = (personData, referenceData, opportunity, isChangeCover = false) => {
  const personObject = mapNamesGenderAndDOB(personData);

  // Certified discount age
  if (personData.abd && personData.abd.cda) {
    personObject.cda = personData.abd.cda;
  } else if (personData.abd) {
    personObject.ageBasedDiscount = personData.abd;
  }

  // Lifetime Health Cover
  const lhcObj = personData.lhc;
  if (lhcObj) {
    personObject.lifetimeHealthCoverOverrule = removeCaeForCertainLhcReasons(lhcObj);

    // Only add date if it exists. it can not exist if value of overruledReason is `NONE`
    if (lhcObj.date) {
      personObject.lifetimeHealthCoverOverrule.date = formatFormDate(lhcObj.date, true);
    }

    // Only add discharge date if it exists
    if (lhcObj.dischargeDate) {
      personObject.lifetimeHealthCoverOverrule.dischargeDate = formatFormDate(lhcObj.dischargeDate, true);
    }
  }

  // Communication preferences
  // Since this is being used for change of cover, communication preferences are not useful when looking for the premium, and communication preferences can actually cause problems with OHI.
  if (!isChangeCover) {
    const phoneNumbers = [];
    personData.preferredContactMethods.forEach((preferredContactMethod) => {
      phoneNumbers.push({ type: preferredContactMethod.type.toLowerCase(), number: removeWhitespaceFromString(preferredContactMethod.number) });
    });
    personObject.phoneNumbers = phoneNumbers;
    personObject.email = personData.email;
    if (isNotBlankString(personData.preferredCommunication)) {
      personObject.preferredCommunicationMode = personData.preferredCommunication.toUpperCase();
    }

    // Set up the address for postal
    personObject.addressPostal = mapAddress(personData.postalAddress, true, personData.POBox);
    if (personObject.addressPostal === null) {
      delete personObject.addressPostal;
    }
  }

  // Transfer funds
  if (personData.transferFromOthers) {
    personObject.previousFunds = mapPreviousFunds(personData);
  }

  // Veteran card
  if (personData.haveVeteranCard) {
    const veteranCardConditions = personData.veteranCardConditionOptions
      .map((conditionOption) => {
        return conditionOption.returnValue;
      })
      .filter(Boolean);
    personObject.veteranCard = { hasVeteranCard: personData.haveVeteranCard, cardType: personData.veteranCardType, acceptedConditions: veteranCardConditions };
  } else {
    personObject.veteranCard = null;
  }

  // Concession cards
  const state = personData.residentialAddress.state?.toLowerCase();
  const dob = personData.DOB;
  const extrasOnly = opportunity.coverType.toLowerCase() === 'extras only';

  if (showConcessionCardSection(state, dob, extrasOnly)) {
    const concessionCardObj = {};
    if (personData.haveConcessionCard) {
      // If age is 65 or above and lives in VIC, WA and NT, then do not need to capture concession card details
      if (calculateAge(dob) >= 65 && (state === 'vic' || state === 'wa' || state === 'nt')) {
        concessionCardObj.cardList = null;
      } // If age is 65 or above, selected Extras only product and is from NSW and ACT, then do not capture concession card details
      else if (calculateAge(dob) >= 65 && extrasOnly && (state === 'nsw' || state === 'act')) {
        concessionCardObj.cardList = null;
      } else {
        concessionCardObj.cardList = personData.selectedConcessionCardOptions.map((concessionCard) => {
          // Since EAI takes in the name of the concession card instead of the code, need to convert the return value to using the name.
          const selectedCardOption = referenceData.concessionCardOptions.filter((cardOption) => {
            return cardOption.returnValue === concessionCard.name;
          });

          // Get the first item since there should always be 1 selected card as they are both driven off the same reference data.
          const selectedCardType = selectedCardOption[0]?.display ?? null;
          const returnConcessionCard = { cardType: selectedCardType, cardNumber: concessionCard.number };
          if (isNotBlankString(concessionCard.expire)) {
            returnConcessionCard.expiryDate = formatFormDate(concessionCard.expire, true);
          }
          return returnConcessionCard;
        });
      }
    } else {
      concessionCardObj.cardList = null;
    }

    concessionCardObj.hasConcessionCard = personData.haveConcessionCard;
    if (concessionCardObj.hasConcessionCard === null && concessionCardObj.cardList === null) {
      personObject.concessionCard = null;
    } else {
      personObject.concessionCard = { ...concessionCardObj };
    }
  } else {
    personObject.concessionCard = null;
  }
  return personObject;
};

const mapPolicyHolder = (policyHolderData, opportunity, referenceData) => {
  if (policyHolderData) {
    const mainPolicyHolder = mapPerson(policyHolderData, referenceData, opportunity);

    // The main policy holder being covered is determined by the composition in the opportunity. They are only not
    // covered in the policy if the composition is children only.
    if (opportunity && opportunity.composition === CoverComposition.CHILDREN_ONLY) {
      mainPolicyHolder.covered = false;
    } else {
      mainPolicyHolder.covered = true;
    }

    mainPolicyHolder.contactId = opportunity.mainPolicyHolder.contactId;

    return mainPolicyHolder;
  }

  return {};
};

const mapPartner = (partnerData, referenceData, opportunity) => {
  const partnerObject = mapPerson(partnerData, referenceData, opportunity);

  // Partner is tranferring from the same fund as the main policy holder
  if (partnerData.sameFund) {
    partnerObject.isSameTransferFund = partnerData.sameFund;
  }
  if (partnerData.sameFund === true){
    partnerObject.previousFunds = null;
  }

  return partnerObject;
};

export const mapChildren = (childrenData, isChangeCover = false, isMemberPortal = false, policyHolder = null) => {
  const childrenArray = [];
  if (childrenData && childrenData.length > 0) {
    childrenData.forEach((childData) => {
      // For change cover, child may not have gender info
      const childObject = mapNamesGenderAndDOB(childData);

      childObject.isStudent = !!childData.isStudent;
      if (!isChangeCover) {
        childObject.isSameTransferFund = !!childData.wasPolicyHolderFund;

        if (childData.transferFromOthers && !childData.wasPolicyHolderFund) {
          childObject.previousFunds = mapPreviousFunds(childData);
        }

        // Logic for setting postal address based on policyHolder postal address
        if (policyHolder && policyHolder.differentPostalAddress) {
          childObject.addressPostal = mapAddress(policyHolder.postalAddress, true, policyHolder.POBox);
        } else if (policyHolder) {
          childObject.addressPostal = mapAddress(policyHolder.residentialAddress, true, false);
        } else {
          childObject.addressPostal = null;
        }

        if (childData.isStudent) {
          // Set the start / end data of the student declaration
          const declarationYear = parseInt(childData.studentDeclarationYear, 10);
          if (!Number.isNaN(declarationYear)) {
            childObject.studentStartDate = `${declarationYear}-03-01`;

            const studentEndYear = `${declarationYear + 1}`;
            let studentEndDay = '28';
            if (isYearLeapYear(studentEndYear)) {
              studentEndDay = '29';
            }
            childObject.studentEndDate = `${studentEndYear}-02-${studentEndDay}`;
          }

          childObject.institutionName = childData.instituteName;
        }
      } else {
        childObject.age = calculateAge(reverseFormatFormDate(childData.DOB));
        childObject.isSameTransferFund = !childData.transferFromOthers;
        if (childData.transferFromOthers) {
          childObject.previousFunds = childData.previousFunds;
        }

        if (childData.isStudent) {
          childObject.studentEndDate = childData.studentEndDate;
          childObject.studentStartDate = childData.studentStartDate;

          if (isMemberPortal) {
            childObject.institutionName = childData.university; // CMS BE expects `institutionName` in quote price payload
          } else {
            childObject.instituteName = childData.university;
          }
        }
      }

      childrenArray.push(childObject);
    });
  }

  if (childrenArray.length) {
    return childrenArray;
  }

  return null;
};

const mapRebate = (rebateData) => {
  const rebateObject = {};
  if (rebateData.isRebateApplied === false) {
    return rebateObject;
  }
  const rebateInfo = {
    rebateTier: rebateData.rebateTier,
    declarationAcceptance: rebateData.declarationAcceptance,
    declarationAcknowledgement: rebateData.declarationAcknowledgement,
    declarationDate: formatFormDate(rebateData.declarationDate, true),
  };
  rebateObject.rebate = { ...rebateInfo };

  if (rebateData.isMedicareEligible === false) {
    return rebateObject;
  }
  const medicareCardObject = {
    cardType: rebateData.medicareCardType,
    cardNumber: removeWhitespaceFromString(rebateData.medicareCardNumber),
    expiryDate: formatFormDate(rebateData.medicareCardExpiryDate, true),
    firstName: rebateData.firstName,
    lastName: rebateData.lastName,
  };
  rebateObject.medicareCard = { ...medicareCardObject };

  return rebateObject;
};

const mapProducts = (quoteData) => {
  const productCodes = quoteData.products.map((product) => {
    return product.code;
  });

  return [{ productCodes, excess: quoteData.excess }];
};

export const mapEligibility = (eligibilityData, type) => {
  const eligibilityObject = {
    eligibilityType: type,
    eligibilitySubject: eligibilityData.serviceConnection,
    subjectName: eligibilityData.serviceConnectionName,
    subjectRank: eligibilityData.subFormData?.veteranInfo?.rank ?? '',
    subjectRelation: eligibilityData.subFormData?.relationship ?? '',
    subjectDepartmentOrCompanyName:
      // as "veteranInfo" without veteran card is undefined then we need to check against undefined and '' then assign the value to "subjectDepartmentOrCompanyName"
      !eligibilityData.subFormData?.veteranInfo?.serviceAgency || eligibilityData.subFormData?.veteranInfo?.serviceAgency === ''
        ? eligibilityData.subFormData?.connectionName
        : eligibilityData.subFormData?.veteranInfo?.serviceAgency,
    subjectEligibilityType: eligibilityData.subFormData?.connectionType,
    subjectServiceType:
      !eligibilityData.subFormData?.veteranInfo?.serviceType || eligibilityData.subFormData?.veteranInfo?.serviceType === ''
        ? eligibilityData.subFormData?.connectionType
        : eligibilityData.subFormData?.veteranInfo?.serviceType,
    enlistmentDate: formatFormDate(eligibilityData.subFormData?.enlistmentDate),
    dischargeDate: formatFormDate(eligibilityData.subFormData?.dischargeDate),
    sercatStatus: eligibilityData.subFormData?.sercatStatus ?? '',
    workConnectionName: eligibilityData.subFormData?.relationName ?? '',
    declarationDate: eligibilityData.declarationDate,
    expiryDate: eligibilityData.declarationExpiryDate,
    declarationIsConfirmed: eligibilityData.declaration,
  };

  return eligibilityObject;
};

const mapFuturePayment = (paymentData, creditCardData) => {
  const paymentObject = {
    premiumPaymentAccount: {
      confirmUseAgreement: paymentData.isDirectDebitServiceAgreement,
      accountType: paymentData.paymentMethod,
    },
    claimsAccount: {
      accountHolderName: paymentData.bankAccountName,
      accountNumberOrToken: paymentData.bankAccountNumber,
      bsb: removeBSBMask(paymentData.bankBSB),
      confirmUseAgreement: paymentData.isPaymentAcknowledgement,
      accountType: PaymentMethods.BANK_ACCOUNT,
    },
  };

  if (paymentData.paymentMethod === PaymentMethods.BANK_ACCOUNT) {
    paymentObject.premiumPaymentAccount.accountHolderName = paymentData.paymentBankAccountName;
    paymentObject.premiumPaymentAccount.accountNumberOrToken = paymentData.paymentBankAccountNumber;
    paymentObject.premiumPaymentAccount.bsb = removeBSBMask(paymentData.paymentBankBSB);
  }

  if (paymentData.paymentMethod === PaymentMethods.CREDIT_CARD) {
    paymentObject.premiumPaymentAccount.accountHolderName = paymentData.nameOnCard;
    paymentObject.premiumPaymentAccount.accountNumberOrToken = creditCardData.creditCardToken;
    paymentObject.premiumPaymentAccount.maskedCreditCardNumber = creditCardData.truncatedCard;
    paymentObject.premiumPaymentAccount.expiryDate = formatFormDate(getDateWithLastDay(creditCardData.expiryYear, creditCardData.expiryMonth), true);
    paymentObject.premiumPaymentAccount.instrumentType = mapIpsiInstrumentTypeArray(creditCardData.instrumentType);
    paymentObject.premiumPaymentAccount.cardCategory = creditCardData.cardCategory;
    paymentObject.premiumPaymentAccount.cardSubCategory = creditCardData.cardSubCategory;
  }

  return paymentObject;
};

// eslint-disable-next-line import/prefer-default-export
export const createMembership = (quoteDetails, termsAndConditionsAcceptance, referenceData) => {
  if (quoteDetails === null || quoteDetails === undefined) {
    logger.error('CreateMembershipUtils - createMembership() is missing quote details');
  }

  const opportunity = quoteDetails.opportunity;
  const contact = opportunity.contact;
  const quote = quoteDetails.quote;

  const membershipObject = {};
  // Get all the data from the different storage keys
  const sessionDataKeys = getStorageKeys(opportunity.composition, opportunity.eligibility.eligibilityType);
  sessionDataKeys.forEach((sessionDataKey) => {
    const sessionKey = sessionDataKey;
    const fetchedData = fetchSessionObject(sessionKey);
    // Convert the key to eligibility keys to a singular key, since there won't be multiple of them when doing the
    // transformation
    const eligibilityStorage = Object.values(STORAGE_KEYS.JOIN_NOW.ELIGIBILITY);
    if (eligibilityStorage.includes(sessionDataKey)) {
      ELIGIBILITY_KEY = sessionDataKey;
    }
    membershipObject[sessionKey] = { ...fetchedData };
  });

  const policyHolder = membershipObject[STORAGE_KEYS.JOIN_NOW.POLICY_HOLDER_FEATURE];
  const paymentInfo = membershipObject[STORAGE_KEYS.JOIN_NOW.FUTURE_PAYMENT.FEATURE];
  const partnerInfo = membershipObject[STORAGE_KEYS.JOIN_NOW.PARTNER_FEATURE];
  const childrenInfo = membershipObject[STORAGE_KEYS.JOIN_NOW.CHILDREN_FEATURE];
  const rebateInfo = membershipObject[STORAGE_KEYS.JOIN_NOW.REBATE_FEATURE];
  const eligibilityInfo = membershipObject[ELIGIBILITY_KEY];

  // Transform the membership object to the new set of JSON data
  const membershipDetails = {
    quoteId: quote.quoteId,
    state: contact.addressState,
    composition: opportunity.composition,
    startDate: formatFormDate(policyHolder.startDateValue, true),
    productList: mapProducts(quote),
    mainPolicyHolder: mapPolicyHolder(policyHolder, opportunity, referenceData),
    myCircumstances: quoteDetails.opportunity.myCircumstances,
  };

  // Set up the address for residential
  const addressResidential = mapAddress(policyHolder.residentialAddress);
  if (addressResidential !== null) {
    membershipDetails.addressResidential = addressResidential;
  }

  if (termsAndConditionsAcceptance !== null && termsAndConditionsAcceptance !== undefined) {
    membershipDetails.termsAndConditionsAcceptance = termsAndConditionsAcceptance;
  }

  if (partnerInfo !== null && partnerInfo !== undefined) {
    const partnerObject = mapPartner(partnerInfo, referenceData, opportunity);
    membershipDetails.partner = partnerObject;
  }

  if (childrenInfo !== null && childrenInfo !== undefined && childrenInfo.children) {
    membershipDetails.children = mapChildren(childrenInfo.children, false, false, policyHolder);
  }

  if (rebateInfo !== null && rebateInfo !== undefined) {
    const rebateObject = mapRebate(rebateInfo);
    membershipDetails.rebate = rebateObject.rebate;
    if (rebateObject.medicareCard) {
      membershipDetails.medicareCard = rebateObject.medicareCard;
    }
  }

  if (eligibilityInfo !== null && eligibilityInfo !== undefined) {
    membershipDetails.eligibility = mapEligibility(eligibilityInfo, opportunity.eligibility.eligibilityType);
  }

  if (paymentInfo !== null && paymentInfo !== undefined) {
    membershipDetails.frequency = paymentInfo.paymentFrequency;
    membershipDetails.paymentStartDate = formatFormDate(paymentInfo.paymentStartDate, true);
    membershipDetails.contributionMethod = 'DD';

    let creditCardInfo = {};

    if (paymentInfo.paymentMethod === PaymentMethods.CREDIT_CARD) {
      creditCardInfo = membershipObject[STORAGE_KEYS.JOIN_NOW.FUTURE_PAYMENT.CREDIT_CARD];
    }

    membershipDetails.premiumPaymentAccount = mapFuturePayment(paymentInfo, creditCardInfo).premiumPaymentAccount;
    membershipDetails.claimsAccount = mapFuturePayment(paymentInfo, creditCardInfo).claimsAccount;
  }

  // logger.log('The membership details are: ', JSON.stringify(membershipDetails));

  return membershipDetails;
};
